/**
 * Working hours & time off (design 2D/2E) for ANY practitioner — the picker
 * over the personnel roster, then the same sections the person's own staff
 * page shows (PractitionerAvailability). Administrator-only in V1 — no
 * request/approve flow. Kept for the roles that manage availability without
 * managing members.
 */

import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import a from '../access/access.module.css';
import { Button, Notice } from '@/ui/kit';
import { SelectField } from '@/ui/formkit';
import { listPersonnel } from '@/core/clinic/setupApi';
import { listMembers } from '@/core/auth/adminApi';
import { Link } from '@tanstack/react-router';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { PERM } from '@/core/authz/permissions';
import { memberDisplayName } from './StaffTab';
import { TimeOffSection, WorkingHoursSection } from './PractitionerAvailability';
import type { MemberRow } from '@/core/auth/adminApi';
import type { PersonnelView } from '@/core/clinic/setupApi';

/** The form's home is PractitionerAvailability; re-exported for its existing tests. */
export { TimeOffForm } from './PractitionerAvailability';

/**
 * ZOY-398: the roster comes from the clinic's own personnel list
 * (clinic.personnel.read) — availability attaches to a personnel profile,
 * and the availability read itself is per-uid. Auth's member list
 * (clinic.members.manage) only ENRICHES labels with display names; without
 * it, uids label honestly as job title + short id, exactly like every other
 * surface. When members ARE readable, non-active members drop out (their
 * schedules are not operational).
 */
export function practitionerOptions(
  personnel: readonly PersonnelView[],
  members: readonly MemberRow[] | null,
  unnamed: string,
): { value: string; label: string }[] {
  const byUid = new Map((members ?? []).map((m) => [m.uid, m]));
  return personnel
    .filter((p) => (members === null ? true : byUid.get(p.uid)?.status === 'active'))
    .map((p) => {
      const m = byUid.get(p.uid);
      // ZOY-363: the personnel row's own displayName is the identity; the
      // member list only backs it up. Never a raw uid as the label.
      const label =
        p.displayName ??
        (m ? memberDisplayName(m) : p.jobTitle ? `${unnamed} · ${p.jobTitle}` : unnamed);
      return { value: p.uid, label };
    });
}

export function HoursTab() {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const authz = useAuthz();
  const clinicId = authz.clinicId ?? '';
  const manage = authz.can(PERM.availabilityManage);
  const canPersonnel = authz.can(PERM.personnelRead);
  const canMembers = authz.can(PERM.membersManage);

  // Names only — the roster never depends on this Auth read (ZOY-398).
  const membersQuery = useQuery({
    queryKey: ['members', clinicId],
    queryFn: () => listMembers(clinicId),
    staleTime: 60_000,
    enabled: canMembers,
  });

  // Working hours & time off attach to a personnel (practitioner) profile: the
  // backend 404s for a member without one, so the personnel list IS the roster.
  const personnelQuery = useQuery({
    queryKey: ['personnel', 'all', clinicId],
    queryFn: () => listPersonnel({ limit: 100, sort: [{ field: 'uid', dir: 'asc' }] }),
    staleTime: 60_000,
    enabled: canPersonnel,
  });
  const options = practitionerOptions(
    personnelQuery.data?.items ?? [],
    canMembers ? (membersQuery.data ?? null) : null,
    t('staff.unnamed'),
  );

  const [uid, setUid] = useState('');
  const effectiveUid = uid || options[0]?.value || '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxInlineSize: 760 }}>
      <p className={a.pageSub}>{t('hours.sub')}</p>

      {!canPersonnel ? (
        <Notice kind="info">{t('hours.needsPersonnel')}</Notice>
      ) : personnelQuery.isError ? (
        <Notice
          kind="err"
          action={
            <Button variant="quiet" onClick={() => void personnelQuery.refetch()}>
              {t('table.retry')}
            </Button>
          }
        >
          {t('rec.sectionFailed')}
        </Notice>
      ) : !personnelQuery.isPending && options.length === 0 ? (
        <Notice kind="info">
          {t('hours.noPractitioners')}
          {canMembers && (
            <>
              {' '}
              <Link to="/clinic-setup/staff">{t('hours.noPractitionersLink')}</Link>
            </>
          )}
        </Notice>
      ) : (
        <SelectField
          label={t('hours.pick')}
          value={effectiveUid}
          onChange={setUid}
          options={options}
        />
      )}

      {effectiveUid && <WorkingHoursSection uid={effectiveUid} manage={manage} />}

      <Notice kind="info">{t('hours.adminOnly')}</Notice>

      {/* ---- Time off (2E) ---- */}
      <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--zc-teal-deep)' }}>
        {t('hours.timeOff')}
      </h2>
      {effectiveUid && <TimeOffSection uid={effectiveUid} manage={manage} />}
    </div>
  );
}
